import React from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { LOGO_URL } from "@/lib/api";
import {
  LayoutDashboard, PlusSquare, Search, ScanLine, Receipt, Truck, Layers, CalendarRange,
  Users, ListChecks, AlertTriangle, Download, UserCog, History, LogOut, LayoutGrid,
} from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";

const items = [
  { to: "/", label: "Dashboard", icon: LayoutDashboard, testid: "nav-dashboard" },
  { to: "/orders/new", label: "Neuer Auftrag", icon: PlusSquare, testid: "nav-new-order" },
  { to: "/orders", label: "Auftrag suchen", icon: Search, testid: "nav-orders" },
  { to: "/ai", label: "KI Screenshot Analyse", icon: ScanLine, testid: "nav-ai" },
  { to: "/bulk", label: "Sammel-Aufträge", icon: Layers, testid: "nav-bulk" },
  { to: "/billing", label: "Mitarbeiterabrechnung", icon: Receipt, testid: "nav-billing" },
  { to: "/monthly", label: "Monatliche Abrechnung", icon: CalendarRange, testid: "nav-monthly" },
  { to: "/vehicles", label: "Fahrzeuge", icon: Truck, testid: "nav-vehicles" },
  { to: "/employees", label: "Mitarbeiter", icon: Users, testid: "nav-employees" },
  { to: "/pricelists", label: "Preislisten", icon: ListChecks, testid: "nav-pricelists" },
  { to: "/conflicts", label: "Konflikte", icon: AlertTriangle, testid: "nav-conflicts" },
  { to: "/export", label: "Exportcenter", icon: Download, testid: "nav-export" },
  { to: "/users", label: "Benutzerverwaltung", icon: UserCog, testid: "nav-users", adminOnly: true },
  { to: "/audit", label: "Änderungsprotokoll", icon: History, testid: "nav-audit" },
];

export default function Sidebar() {
  const { user, isAdmin, logout } = useAuth();
  const nav = useNavigate();
  return (
    <aside
      data-testid="sidebar"
      className="skalar-grid w-64 h-screen sticky top-0 flex flex-col text-white"
      style={{ background: "#0A2540" }}
    >
      <div className="px-6 py-4 border-b border-white/10">
        <button
          onClick={() => nav("/modules")}
          data-testid="sidebar-to-modules"
          className="w-full flex items-center gap-2 px-3 py-2 rounded-md text-sm font-semibold bg-white/10 hover:bg-white/15 transition-colors"
        >
          <LayoutGrid className="h-4 w-4" /> Zur Modulauswahl
        </button>
      </div>
      <div className="px-6 py-4 border-b border-white/10">
        <div className="flex items-center gap-2.5">
          <div className="w-10 h-10 rounded-md flex items-center justify-center bg-white p-1">
            <img src={LOGO_URL} alt="ST" className="w-full h-full object-contain" />
          </div>
          <div className="leading-tight">
            <div className="text-base font-bold tracking-wide">Leistungsabrechnung</div>
            <div className="text-[11px] uppercase opacity-60 tracking-widest">Aufmaß · Abrechnung</div>
          </div>
        </div>
      </div>
      <nav className="flex-1 overflow-y-auto p-3 space-y-0.5">
        {items.filter((i) => !i.adminOnly || isAdmin).map((i) => (
          <NavLink
            key={i.to}
            to={i.to}
            end={i.to === "/"}
            className={({ isActive }) => `nav-item ${isActive ? "active" : ""}`}
            data-testid={i.testid}
          >
            <i.icon className="h-4 w-4" strokeWidth={2} />
            <span>{i.label}</span>
          </NavLink>
        ))}
      </nav>
      <div className="p-3 border-t border-white/10">
        <div className="flex items-center justify-between px-2 py-2">
          <div className="leading-tight">
            <div className="text-sm font-semibold truncate max-w-[140px]">{user?.name}</div>
            <div className="text-[11px] opacity-60 uppercase tracking-wider">{user?.role}</div>
          </div>
          <button
            data-testid="logout-btn"
            onClick={async () => { await logout(); nav("/login"); }}
            className="p-2 rounded-md hover:bg-white/10"
            aria-label="Abmelden"
          >
            <LogOut className="h-4 w-4" />
          </button>
        </div>
      </div>
    </aside>
  );
}
